<template>
  <div class="admin-container">
    <div class="trainer-academy-modules">
      <div class="modules-header">
        <h1>Trainer Academy Modules</h1>
        <p class="modules-description">
          You can add and delete all modules in here
        </p>
      </div>

      <div class="how-to-use-banner">
        <div class="how-to-use-icon">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V15H13V17ZM13 13H11V7H13V13Z"
              fill="white"
            />
          </svg>
        </div>
        <div class="how-to-use-content">
          <h3>How to use</h3>
          <p>
            Use drag & drop to quickly reposition cards. You can also type a
            position number to move items directly to that spot.
          </p>
        </div>
      </div>

      <div class="modules-content">
        <BaseButton
          variant="primary"
          class="add-module-button"
          @click="handleAddModule"
        >
          <div class="button-content-wrapper">
            <span>Add New Module</span>
            <img
              src="/images/admin/plus-circle.svg"
              alt="Add"
              class="button-icon"
            />
          </div>
        </BaseButton>

        <div v-if="loading" class="loading-container">
          <p>Loading modules...</p>
        </div>
        <div v-else class="modules-list" ref="modulesListRef">
          <div
            v-for="(module, index) in modules"
            :key="module.id"
            class="module-card"
            :class="{ dragging: draggedIndex === index }"
            :draggable="true"
            @dragstart="handleDragStart(index, $event)"
            @dragover.prevent="handleDragOver(index, $event)"
            @drop="handleDrop(index, $event)"
            @dragend="handleDragEnd"
          >
            <div class="module-header">
              <div class="module-left">
                <div class="drag-handle" @mousedown.stop>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 5C9 5.55228 8.55228 6 8 6C7.44772 6 7 5.55228 7 5C7 4.44772 7.44772 4 8 4C8.55228 4 9 4.44772 9 5Z"
                      fill="#003d6a"
                    />
                    <path
                      d="M9 12C9 12.5523 8.55228 13 8 13C7.44772 13 7 12.5523 7 12C7 11.4477 7.44772 11 8 11C8.55228 11 9 11.4477 9 12Z"
                      fill="#003d6a"
                    />
                    <path
                      d="M9 19C9 19.5523 8.55228 20 8 20C7.44772 20 7 19.5523 7 19C7 18.4477 7.44772 18 8 18C8.55228 18 9 18.4477 9 19Z"
                      fill="#003d6a"
                    />
                    <path
                      d="M17 5C17 5.55228 16.5523 6 16 6C15.4477 6 15 5.55228 15 5C15 4.44772 15.4477 4 16 4C16.5523 4 17 4.44772 17 5Z"
                      fill="#003d6a"
                    />
                    <path
                      d="M17 12C17 12.5523 16.5523 13 16 13C15.4477 13 15 12.5523 15 12C15 11.4477 15.4477 11 16 11C16.5523 11 17 11.4477 17 12Z"
                      fill="#003d6a"
                    />
                    <path
                      d="M17 19C17 19.5523 16.5523 20 16 20C15.4477 20 15 19.5523 15 19C15 18.4477 15.4477 18 16 18C16.5523 18 17 18.4477 17 19Z"
                      fill="#003d6a"
                    />
                  </svg>
                </div>
                <div class="module-number-badge">
                  {{ index + 1 }}
                </div>
                <div class="module-info">
                  <div class="module-name">Module {{ index + 1 }}</div>
                  <div
                    v-if="!editingModuleId || editingModuleId !== module.id"
                    class="module-title-row"
                  >
                    <span class="module-title">
                      {{ module.title || "Title Module" }}
                    </span>
                    <img
                      src="/images/edit-pencil.svg"
                      alt="Edit"
                      class="edit-icon"
                      @click="startEdit(module)"
                    />
                  </div>
                  <div v-else class="module-edit-container">
                    <div class="module-edit-input-group">
                      <input
                        v-model="editingTitle"
                        @keyup.enter="saveEdit(module.id)"
                        class="module-title-input"
                        :ref="(el) => setTitleInputRef(module.id, el)"
                      />
                      <div class="module-edit-actions">
                        <BaseButton
                          variant="primary"
                          className="module-save-button"
                          @click="saveEdit(module.id)"
                          :loading="actionsLoading"
                        >
                          Save
                        </BaseButton>
                        <BaseButton
                          variant="outline"
                          className="module-cancel-button"
                          @click="cancelEdit"
                          :disabled="actionsLoading"
                        >
                          Cancel
                        </BaseButton>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="
                      module.active_trainers !== undefined &&
                      module.active_trainers !== null
                    "
                    class="trainers-badge"
                    :class="{ disabled: module.active_trainers === 0 }"
                  >
                    {{ module.active_trainers }} Users learning this module
                  </div>
                </div>
              </div>
              <img
                src="/images/delete.svg"
                alt="Delete"
                class="delete-icon"
                @click="handleDelete(module)"
              />
            </div>

            <div class="module-divider"></div>

            <div class="module-reorder">
              <span class="reorder-label">Jump to position:</span>
              <div class="reorder-input-group">
                <input
                  type="number"
                  v-model="module.positionInput"
                  @keyup.enter="handleJumpToPosition(module.id, index)"
                  class="reorder-input"
                  :min="1"
                  :max="modules.length"
                />
                <BaseButton
                  variant="primary"
                  class="reorder-button"
                  @click="handleJumpToPosition(module.id, index)"
                >
                  Reorder
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ConfirmationModal
      v-model="showDeleteModal"
      variant="danger"
      title="Remove Module"
      message="Are you sure want to remove this module?"
      confirm-text="Confirm"
      cancel-text="Back"
      :loading="deleting"
      @confirm="confirmDelete"
      @cancel="showDeleteModal = false"
    />

    <UploadModuleModal
      v-model="showUploadModal"
      @save="handleSaveModule"
      @cancel="showUploadModal = false"
    />
  </div>
</template>

<script setup>
import UploadModuleModal from "@/components/admin/trainer-academy/UploadModuleModal.vue";
import BaseButton from "@/components/common/BaseButton.vue";
import ConfirmationModal from "@/components/common/ConfirmationModal.vue";
import trainerAcademyService from "@/services/trainerAcademyService";
import { getApiError } from "@/utils/apiError";
import { inject, nextTick, onMounted, ref } from "vue";

const notify = inject("notify");

const modules = ref([]);

const loading = ref(false);
const actionsLoading = ref(false);

const draggedIndex = ref(null);
const draggedOverIndex = ref(null);
const modulesListRef = ref(null);
const editingModuleId = ref(null);
const editingTitle = ref("");
const titleInputRefs = ref({});
const showDeleteModal = ref(false);
const showUploadModal = ref(false);
const moduleToDelete = ref(null);
const deleting = ref(false);

const handleDragStart = (index, event) => {
  draggedIndex.value = index;
  event.dataTransfer.effectAllowed = "move";
  event.dataTransfer.setData("text/html", event.target.outerHTML);
  event.target.style.opacity = "0.5";
};

const handleDragOver = (index, event) => {
  event.preventDefault();
  draggedOverIndex.value = index;
};

const updateModuleOrders = async (modulesToUpdate) => {
  try {
    const updatePromises = modulesToUpdate.map((module, idx) => {
      const newOrder = idx + 1;
      if (module.position !== newOrder) {
        const formData = new FormData();
        formData.append("order", newOrder);
        return trainerAcademyService.adminUpdateModule(module.id, formData);
      }
      return Promise.resolve();
    });

    await Promise.all(updatePromises);

    modulesToUpdate.forEach((m, idx) => {
      m.position = idx + 1;
      m.positionInput = idx + 1;
    });

    return true;
  } catch (error) {
    console.error("Error updating module orders:", error);
    throw error;
  }
};

const handleDrop = async (index, event) => {
  event.preventDefault();

  if (draggedIndex.value === null || draggedIndex.value === index) {
    return;
  }

  const draggedModule = modules.value[draggedIndex.value];
  const newModules = [...modules.value];
  const oldModules = [...modules.value];

  newModules.splice(draggedIndex.value, 1);
  newModules.splice(index, 0, draggedModule);

  modules.value = newModules;

  modules.value.forEach((m, idx) => {
    m.positionInput = idx + 1;
  });

  draggedIndex.value = null;
  draggedOverIndex.value = null;

  try {
    await updateModuleOrders(modules.value);
    notify.success("Module order updated successfully");
  } catch (error) {
    modules.value = oldModules;
    modules.value.forEach((m, idx) => {
      m.positionInput = m.position || idx + 1;
    });
    const errorMessage = getApiError(
      error,
      "Failed to update module order"
    ).message;
    notify.error(errorMessage, "Error");
  }
};

const handleDragEnd = (event) => {
  event.target.style.opacity = "";
  draggedIndex.value = null;
  draggedOverIndex.value = null;
};

const setTitleInputRef = (moduleId, el) => {
  if (el) {
    titleInputRefs.value[moduleId] = el;
  }
};

const startEdit = (module) => {
  editingModuleId.value = module.id;
  editingTitle.value = module.title || "Title Module";
  nextTick(() => {
    const inputRef = titleInputRefs.value[module.id];
    if (inputRef && inputRef.focus) {
      inputRef.focus();
      inputRef.select();
    }
  });
};

const saveEdit = async (moduleId) => {
  const module = modules.value.find((m) => m.id === moduleId);
  actionsLoading.value = true;
  if (module) {
    const newTitle = editingTitle.value || "Title Module";

    try {
      const formData = new FormData();
      formData.append("title", newTitle);

      await trainerAcademyService.adminUpdateModule(moduleId, formData);
      module.title = newTitle;
      notify.success("Module title updated successfully");
      editingModuleId.value = null;
      editingTitle.value = "";
    } catch (error) {
      console.error("Error updating module title:", error);
      const errorMessage = getApiError(
        error,
        "Failed to update module title"
      ).message;
      notify.error(errorMessage, "Error");
      editingTitle.value = module.title;
    } finally {
      actionsLoading.value = false;
    }
  }
};

const cancelEdit = () => {
  editingModuleId.value = null;
  editingTitle.value = "";
};

const handleDelete = (module) => {
  moduleToDelete.value = module;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  if (!moduleToDelete.value) return;

  deleting.value = true;
  try {
    await trainerAcademyService.adminDeleteModule(moduleToDelete.value.id);

    const index = modules.value.findIndex(
      (m) => m.id === moduleToDelete.value.id
    );
    if (index !== -1) {
      modules.value.splice(index, 1);

      modules.value.forEach((m, idx) => {
        m.positionInput = idx + 1;
        m.position = idx + 1;
      });

      notify.success("Module deleted successfully");
    }
  } catch (error) {
    const errorMessage = getApiError(error, "Failed to delete module").message;
    notify.error(errorMessage, "Error");
  } finally {
    deleting.value = false;
    showDeleteModal.value = false;
    moduleToDelete.value = null;
  }
};

const handleAddModule = () => {
  showUploadModal.value = true;
};

const handleSaveModule = async (data) => {
  try {
    const { file, scorm, tab } = data;
    const maxOrder =
      modules.value.length > 0
        ? Math.max(...modules.value.map((m) => m.position || 0))
        : 0;
    const nextOrder = maxOrder + 1;

    let response;
    if (tab === "manual") {
      const formData = new FormData();
      formData.append(
        "title",
        file.name.replace(/\.[^/.]+$/, "") || "Title Module"
      );
      formData.append("status", "published");
      formData.append("file", file);
      formData.append("order", nextOrder);

      response = await trainerAcademyService.adminAddModule(formData);
    } else {
      const scormData = {
        uuid: scorm.uuid || scorm.value,
        origin_file: scorm.origin_file || scorm.scorm_origin || "",
        status: "published",
        title: scorm.title || scorm.text || "Title Module",
        order: nextOrder,
      };

      response = await trainerAcademyService.adminAddExistingScorm(scormData);
    }

    const responseData = response?.data?.data;
    if (responseData) {
      const newModule = {
        id: responseData.id,
        title: responseData.title || "Title Module",
        position: responseData.order || nextOrder,
        positionInput: responseData.order || nextOrder,
        status:
          responseData.status || (tab === "scorm" ? "draft" : "published"),
        active_trainers: responseData.active_trainers || 0,
      };

      modules.value.push(newModule);
      modules.value.sort((a, b) => a.position - b.position);
      showUploadModal.value = false;
      notify.success("Module added successfully");
    }
  } catch (error) {
    console.error("Error saving module:", error);
    const errorMessage = getApiError(error, "Failed to add module").message;
    notify.error(errorMessage, "Error");
  }
};

const handleJumpToPosition = async (moduleId, currentIndex) => {
  const module = modules.value.find((m) => m.id === moduleId);
  if (!module) return;

  const targetPosition = parseInt(module.positionInput);
  if (
    isNaN(targetPosition) ||
    targetPosition < 1 ||
    targetPosition > modules.value.length
  ) {
    module.positionInput = currentIndex + 1;
    return;
  }

  const targetIndex = targetPosition - 1;

  if (currentIndex === targetIndex) {
    return;
  }

  const newModules = [...modules.value];
  const oldModules = [...modules.value];
  const moduleToMove = newModules[currentIndex];

  newModules.splice(currentIndex, 1);
  newModules.splice(targetIndex, 0, moduleToMove);

  modules.value = newModules;

  modules.value.forEach((m, idx) => {
    m.positionInput = idx + 1;
  });

  try {
    await updateModuleOrders(modules.value);
    notify.success("Module order updated successfully");
  } catch (error) {
    modules.value = oldModules;
    modules.value.forEach((m, idx) => {
      m.positionInput = m.position || idx + 1;
    });
    const errorMessage = getApiError(
      error,
      "Failed to update module order"
    ).message;
    notify.error(errorMessage, "Error");
  }
};

const fetchModules = async () => {
  loading.value = true;
  try {
    const response = await trainerAcademyService.adminGetModules();

    if (response && response.data && response.data.data.rows) {
      modules.value = response.data.data.rows.map((module, index) => ({
        id: module.id,
        title: module.title || "Title Module",
        position: module.order || index + 1,
        positionInput: module.order || index + 1,
        status: module.status || "draft",
        active_trainers: module.active_trainers || 0,
      }));

      modules.value.sort((a, b) => a.position - b.position);
    }
  } catch (error) {
    console.error("Error fetching modules:", error);
    const errorMessage = getApiError(error, "Failed to load modules").message;
    notify.error(errorMessage, "Error");
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchModules();
});
</script>

<style scoped>
.trainer-academy-modules {
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
}

.modules-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.modules-header h1 {
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: 36px;
  font-weight: 700;
  line-height: 44px;
  margin: 0;
}

.modules-description {
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  margin: 0;
}

.how-to-use-banner {
  background-color: #797af4;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.how-to-use-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.how-to-use-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.how-to-use-content h3 {
  color: white;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  margin: 0;
}

.how-to-use-content p {
  color: white;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  margin: 0;
}

.modules-content {
  background: white;
  border-radius: 16px;
  padding: 16px;
  box-shadow:
    0px 1px 2px 0px rgba(0, 0, 0, 0.3),
    0px 2px 6px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.add-module-button {
  align-self: flex-start;
  background-color: var(--primary-blue) !important;
  color: white !important;
  border-radius: 12px;
  height: 56px;
  padding: 10px 16px;
}

.add-module-button:hover {
  background-color: #00599a !important;
}

.button-content-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
}

.button-icon {
  width: 24px;
  height: 24px;
  filter: brightness(0) invert(1);
}

.modules-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.module-card {
  background: white;
  border: 1px solid #4c4ddc;
  border-radius: 24px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  cursor: move;
  transition: all 0.2s;
}

.module-card:hover {
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
}

.module-card.dragging {
  opacity: 0.5;
}

.module-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.module-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.drag-handle {
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.drag-handle:active {
  cursor: grabbing;
}

.module-number-badge {
  background-color: #797af4;
  color: white;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  flex-shrink: 0;
}

.module-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.module-name {
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
}

.trainers-badge {
  background-color: #56b693;
  color: white;
  font-family: "League Spartan", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  padding: 10px 16px;
  border-radius: 999px;
  max-width: 200px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 4px;
  white-space: nowrap;
}

.trainers-badge.disabled {
  background-color: #dfe2e7;
  color: #6c737f;
}
.module-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.module-title {
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
}

.module-edit-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.module-edit-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.module-title-input {
  background: white;
  border: 1px solid #6c737f;
  border-radius: 8px;
  height: 40px;
  width: 250px;
  padding: 4px 16px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  line-height: 24px;
  color: #3b4648;
  outline: none;
}

.module-title-input:focus {
  border-color: var(--primary-blue);
}

.module-edit-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.module-save-button {
  background-color: var(--primary-blue) !important;
  color: white !important;
  border-radius: 8px;
  height: 40px;
  padding: 10px 16px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 700;
  line-height: 20px;
  border: none;
}

.module-save-button:hover {
  background-color: #00599a !important;
}

.module-cancel-button {
  background-color: transparent !important;
  color: #40444b !important;
  border: 1px solid var(--primary-blue) !important;
  border-radius: 8px;
  height: 40px;
  padding: 10px 16px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 700;
  line-height: 20px;
}

.module-cancel-button:hover {
  background-color: var(--primary-blue) !important;
  color: white !important;
}

.edit-icon {
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
}

.delete-icon {
  width: 24px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

.module-divider {
  height: 1px;
  width: 100%;
  background-color: #e0e0e0;
}

.module-reorder {
  display: flex;
  align-items: center;
  gap: 4px;
}

.reorder-label {
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap;
}

.reorder-input-group {
  display: flex;
  align-items: stretch;
}

.reorder-input {
  background: white;
  border: 1px solid #6c737f;
  border-radius: 8px 0 0 8px;
  height: 40px;
  width: 161px;
  padding: 4px 16px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  line-height: 24px;
  color: #3b4648;
  text-align: center;
  outline: none;
}

.reorder-input:focus {
  border-color: var(--primary-blue);
}

.reorder-button {
  background-color: #00599a !important;
  color: white !important;
  border-radius: 0 8px 8px 0;
  height: 40px;
  padding: 10px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  line-height: 24px;
  white-space: nowrap;
  border: none;
}

.reorder-button:hover {
  background-color: #006bb3 !important;
}

.publish-button {
  align-self: flex-end;
  background-color: var(--primary-blue) !important;
  color: white !important;
  height: 56px;
  padding: 10px 16px;
  border-radius: 12px;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
}

.publish-button:hover {
  background-color: #00599a !important;
}

@media (max-width: 768px) {
  .modules-header h1 {
    font-size: var(--fs-xxl);
    line-height: 36px;
  }

  .modules-description {
    font-size: 18px;
    line-height: 24px;
  }

  .how-to-use-content h3 {
    font-size: var(--fs-big);
    line-height: 28px;
  }

  .how-to-use-content p {
    font-size: 18px;
    line-height: 24px;
  }

  .module-left {
    flex-wrap: wrap;
  }

  .module-reorder {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .reorder-input-group {
    width: 100%;
  }

  .reorder-input {
    flex: 1;
  }
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  color: var(--primary-blue);
  font-family: "League Spartan", sans-serif;
  font-size: 18px;
  font-weight: 400;
}
</style>
